import React from 'react';
import {
  IMarketplaceTilesUnitsBreakdown,
} from 'src/typings/marketplace';
import { translations } from 'src/constants/translations';
import { updateTranslatedField } from 'src/components/profileComponents/Marketplace/components/Filters/helpers';
import { BoxFormTextField } from 'src/components/profileComponents/FormTextField';
import { BoxFormAutocomplete } from 'src/components/profileComponents/FormAutocomplete';
import {
  GroupWrapperFiltersAction,
} from 'src/components/profileComponents/Marketplace/components/GroupWrapperActions/GroupWrapperFiltersAction';
import { useStyles as mainUseStyles } from 'src/components/profileComponents/Marketplace/components/Common/styles';
import { GroupWrapper } from 'src/components/profileComponents/Marketplace/components/GroupWrapper';
import {
  GroupWrapperCollapseState,
} from 'src/components/profileComponents/Marketplace/components/GroupWrapperActions/GroupWrapperFiltersAction/types';
import {
  IComplexProperty, IMarketplaceProperty, RoundingMethods, roundingMethodsOptions, roundingMethodsOptionsMap,
  sortOrderOptions,
  sortOrderOptionsMap,
  SortOrderType, ThousandsSeparator, thousandsSeparatorOptions, thousandsSeparatorOptionsMap,
} from 'src/components/profileComponents/Marketplace/types';
import {
  IDevelopmentSpecificationPropChange,
} from 'src/components/profileComponents/Complexes/components/ProjectSpecification/types';
import { IMarketplaceConfigUnitsBreakDownDynamic } from 'src/typings/marketplaceTile';
import Box from '@mui/material/Box';
import {
  useStyles,
} from './styles';

interface Props {
  item: IMarketplaceTilesUnitsBreakdown;
  lang: string;
  complexPropertyOptions: IDevelopmentSpecificationPropChange<IComplexProperty>[]
  onChange: (item: IMarketplaceTilesUnitsBreakdown) => void,
}
export const TileUnitsBreakdown: React.FC<Props> = ({
  item, lang, complexPropertyOptions, onChange,
}) => {
  const styles = useStyles();
  const mainStyles = mainUseStyles();
  const [collapseState, setCollapseState] = React.useState<GroupWrapperCollapseState>(GroupWrapperCollapseState.OPEN);
  const [rocketState, setRocketState] = React.useState<boolean>(item.isActive);

  const rocketChange = (state: boolean) => {
    onChange({ ...item, isActive: state });
    setRocketState(state);
  };

  return (
    <Box style={{ backgroundColor: 'white' }}>
      <GroupWrapper
        name="Units Breakdown (minimum values)"
        customStyles={!rocketState ? mainStyles.customGroupWrapperStyles : undefined}
        actions={(
          <GroupWrapperFiltersAction
            collapseState={collapseState}
            rocketState={rocketState}
            onRocket={rocketChange}
            onCollapse={(state) => setCollapseState(state)}
          />
        )}
      >
        { collapseState === GroupWrapperCollapseState.OPEN && (
          <div className={styles.tileLocationBreakdownWrapper}>
            <div className={styles.section}>
              <div className={styles.shortWrapper}>
                <BoxFormTextField
                  label={translations.prefix}
                  name="prefix"
                  defaultValue={item.prefix?.[lang]}
                  onChange={(event) => {
                    onChange(updateTranslatedField(item, 'prefix', lang, event.target.value));
                  }}
                />
              </div>
              <BoxFormAutocomplete
                label={translations.chooseGroupingProperty}
                name="property"
                defaultValue={{
                  label: item.property?.translates?.[lang] || item.property?.systemName,
                  value: item.property,
                }}
                onChange={(event: IDevelopmentSpecificationPropChange<IMarketplaceProperty>) => {
                  onChange({ ...item, property: event.value });
                }}
                options={complexPropertyOptions}
              />
              <div className={styles.shortWrapper}>
                <BoxFormTextField
                  label={translations.suffix}
                  name="suffix"
                  defaultValue={item.suffix?.[lang]}
                  onChange={(event) => {
                    onChange(updateTranslatedField(item, 'suffix', lang, event.target.value));
                  }}
                />
              </div>
              <div className={styles.shortWrapper}>
                <BoxFormAutocomplete
                  label={translations.sortOrder}
                  name="sortOrder"
                  defaultValue={
                    item.propertyValuesOrder
                      ? {
                        label: sortOrderOptionsMap[item.propertyValuesOrder],
                        value: item.propertyValuesOrder,
                      }
                      : undefined
                  }
                  options={sortOrderOptions}
                  onChange={(event: IDevelopmentSpecificationPropChange<SortOrderType>) => {
                    onChange({ ...item, propertyValuesOrder: event.value });
                  }}
                />
              </div>
            </div>
            { item.groupedDynamicPropertyWrappers.map((subProperty, index) => (
              <div className={styles.section} key={subProperty.id}>
                <BoxFormTextField
                  label={translations.prefix}
                  name="fromPrefix"
                  defaultValue={subProperty.fromPrefix?.[lang]}
                  onChange={(event) => {
                    onChange({
                      ...item,
                      groupedDynamicPropertyWrappers: item.groupedDynamicPropertyWrappers.map((current) => (
                        current.id === subProperty.id
                          ? updateTranslatedField(current, 'fromPrefix', lang, event.target.value)
                          : current
                      )),
                    });
                  }}
                />
                <BoxFormAutocomplete
                  label={!index ? translations.chooseFirstDescriptionProperty : translations.chooseSecondDescriptionProperty}
                  name="property"
                  defaultValue={{
                    label: subProperty.property?.translates?.[lang] || subProperty.property?.systemName,
                    value: subProperty.property,
                  }}
                  onChange={(event: IDevelopmentSpecificationPropChange<IMarketplaceProperty>) => {
                    onChange({ ...item, property: event.value });
                  }}
                  options={complexPropertyOptions}
                />
                <BoxFormTextField
                  label={translations.uom}
                  name="uom"
                  defaultValue={subProperty.uomText?.[lang]}
                  onChange={(event) => {
                    onChange({
                      ...item,
                      groupedDynamicPropertyWrappers: item.groupedDynamicPropertyWrappers.map((current) => (
                        current.id === subProperty.id
                          ? updateTranslatedField(current, 'uomText', lang, event.target.value)
                          : current
                      )),
                    });
                  }}
                />
                <BoxFormAutocomplete
                  label={translations.roundingMethod}
                  name="roundingMethod"
                  defaultValue={{
                    label: roundingMethodsOptionsMap[subProperty.numberTransformationConfig.precision],
                    value: subProperty.numberTransformationConfig.precision,
                  }}
                  onChange={(event: IDevelopmentSpecificationPropChange<RoundingMethods>) => {
                    onChange({
                      ...item,
                      groupedDynamicPropertyWrappers: item.groupedDynamicPropertyWrappers.map((current) => (
                        current.id === subProperty.id ? {
                          ...current,
                          numberTransformationConfig: {
                            ...current.numberTransformationConfig,
                            precision: event.value,
                          },
                        } : current
                      )),
                    });
                  }}
                  options={roundingMethodsOptions}
                />
                <BoxFormAutocomplete
                  label={translations.thousandsSeparator}
                  name="thousandsSeparator"
                  defaultValue={{
                    label: thousandsSeparatorOptionsMap[subProperty.numberTransformationConfig.thousandsSeparator],
                    value: subProperty.numberTransformationConfig.thousandsSeparator,
                  }}
                  onChange={(event: IDevelopmentSpecificationPropChange<ThousandsSeparator>) => {
                    onChange({
                      ...item,
                      groupedDynamicPropertyWrappers: item.groupedDynamicPropertyWrappers.map((current) => (
                        current.id === subProperty.id ? {
                          ...current,
                          numberTransformationConfig: {
                            ...current.numberTransformationConfig,
                            thousandsSeparator: event.value,
                          },
                        } : current
                      )),
                    });
                  }}
                  options={thousandsSeparatorOptions}
                />
              </div>
            )) }
          </div>
        ) }
      </GroupWrapper>
    </Box>
  );
};
